import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { StatusBadge } from '../components/StatusBadge';
import { CONTENT_BUCKET } from '../config';
import { useAuth } from '../context/AuthContext';
import { loadReferences } from '../lib/data';
import { errorMessage } from '../lib/errors';
import { exactArrayBuffer } from '../lib/hash';
import { buildAndPublishRelease, loadActiveManifest, type ReleaseBuildResult } from '../lib/releaseBuilder';
import { buildSeedFromManifest, type SeedBuildResult } from '../lib/seedBuilder';
import { supabase } from '../lib/supabase';
import { formatBytes, formatDate } from '../lib/text';
import type { ContentManifestV2, ContentPack, ContentRelease, Language } from '../lib/types';

export function ReleasesPage() {
  const { user } = useAuth();
  const [releases, setReleases] = useState<ContentRelease[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [languageCode, setLanguageCode] = useState('fr');
  const [contentPackId, setContentPackId] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [result, setResult] = useState<ReleaseBuildResult | null>(null);
  const [manifest, setManifest] = useState<ContentManifestV2 | null>(null);
  const [seedResult, setSeedResult] = useState<SeedBuildResult | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    const [refs, releaseResult, activeManifest] = await Promise.all([
      loadReferences(),
      supabase.from('content_releases').select('*').order('created_at', { ascending: false }).limit(50),
      loadActiveManifest(),
    ]);
    if (releaseResult.error) throw releaseResult.error;
    setLanguages(refs.languages);
    setPacks(refs.packs);
    setReleases((releaseResult.data ?? []) as ContentRelease[]);
    setManifest(activeManifest);
    if (!contentPackId) {
      const firstPack = refs.packs.find((pack) => pack.language_code === languageCode) ?? refs.packs[0];
      setContentPackId(firstPack?.id ?? '');
    }
  };

  useEffect(() => {
    void load().catch((caught) => setError(errorMessage(caught)));
  }, []);

  const filteredPacks = useMemo(
    () => packs.filter((pack) => pack.language_code === languageCode),
    [languageCode, packs],
  );

  const publish = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return;
    if (!window.confirm(`Confirmer la publication réelle du pack ${contentPackId} ?`)) return;
    setBusy(true);
    setError('');
    setResult(null);
    setProgress(0);
    try {
      const publicationName = name.trim() || `Contenu ${languageCode.toUpperCase()} · ${new Date().toLocaleDateString('fr-FR')}`;
      const built = await buildAndPublishRelease({
        name: publicationName,
        notes: notes.trim(),
        languageCode,
        contentPackId,
        userId: user.id,
        onProgress: (message, value) => {
          setProgressLabel(message);
          setProgress(value);
        },
      });
      setResult(built);
      setName('');
      setNotes('');
      await load();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const downloadSeed = async () => {
    setBusy(true);
    setError('');
    setSeedResult(null);
    try {
      const active = await loadActiveManifest();
      if (!active) throw new Error('Le manifeste actif est absent.');
      const built = await buildSeedFromManifest(active);
      const url = URL.createObjectURL(new Blob([exactArrayBuffer(built.bytes)], { type: 'application/vnd.sqlite3' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'findex_seed.sqlite';
      anchor.click();
      URL.revokeObjectURL(url);
      setSeedResult(built);
      setManifest(active);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const activeManifestUrl = supabase.storage.from(CONTENT_BUCKET).getPublicUrl('manifest.json').data.publicUrl;

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Distribution</p>
          <h1>Publications</h1>
          <p>Génère un pack SQLite compressé et active son manifeste pour les joueurs.</p>
        </div>
        <a className="button secondary" href={activeManifestUrl} target="_blank" rel="noreferrer">
          Ouvrir le manifeste actif
        </a>
      </header>

      {error && <div className="alert error">{error}</div>}
      {result && (
        <div className="alert success release-success">
          <strong>Publication #{result.release.id} activée.</strong>
          <span>Schéma v{result.schemaVersion} · pack v{result.packVersion} · {result.puzzleCount.toLocaleString('fr-FR')} énigmes · {formatBytes(result.packBytes)} · SHA-256 {result.sha256}</span>
          <a href={result.manifestUrl} target="_blank" rel="noreferrer">Voir le manifeste</a>
        </div>
      )}
      {seedResult && (
        <div className="alert success">
          Seed téléchargée : {seedResult.packCount} pack(s), {seedResult.puzzleCount} énigmes, {formatBytes(seedResult.bytes.byteLength)}, SHA-256 {seedResult.sha256}, manifeste du {formatDate(seedResult.manifestGeneratedAt)}.
        </div>
      )}

      <section className="panel table-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Manifeste actif</p>
            <h2>Packs distribués · schéma v{manifest?.schemaVersion ?? 2}</h2>
            <p className="muted"><a href={activeManifestUrl} target="_blank" rel="noreferrer">{activeManifestUrl}</a></p>
          </div>
          <button className="button secondary" disabled={busy} onClick={() => void downloadSeed()}>
            Télécharger la seed SQLite
          </button>
        </div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>Pack</th><th>Langue</th><th>Version</th><th>Énigmes</th><th>Taille GZIP</th><th>SHA-256</th></tr></thead>
            <tbody>
              {!manifest || manifest.packs.length === 0 ? (
                <tr><td colSpan={6} className="empty-cell">Aucun pack actif publié.</td></tr>
              ) : manifest.packs.map((item) => (
                <tr key={item.id}>
                  <td><a className="text-link" href={item.url}>{item.id}</a></td>
                  <td>{item.languageCode}</td><td>{item.version}</td><td>{item.puzzleCount}</td>
                  <td>{formatBytes(item.sizeBytes)}</td><td><code>{item.sha256}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="two-column-grid release-layout">
        <form className="panel form-stack" onSubmit={publish}>
          <div>
            <p className="eyebrow">Nouvelle version</p>
            <h2>Publier un pack</h2>
            <p className="muted">Sont incluses les énigmes au statut <code>approved</code> ou <code>published</code>.</p>
          </div>
          <label>
            Nom de la publication
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Contenu français – version 1" />
          </label>
          <label>
            Langue
            <select
              value={languageCode}
              onChange={(event) => {
                const nextLanguage = event.target.value;
                setLanguageCode(nextLanguage);
                setContentPackId(packs.find((pack) => pack.language_code === nextLanguage)?.id ?? '');
              }}
            >
              {languages.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}
            </select>
          </label>
          <label>
            Pack
            <select required value={contentPackId} onChange={(event) => setContentPackId(event.target.value)}>
              <option value="">Sélectionner…</option>
              {filteredPacks.map((pack) => <option key={pack.id} value={pack.id}>{pack.name}</option>)}
            </select>
          </label>
          <label>
            Notes internes
            <textarea rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </label>

          {busy && (
            <div className="progress-panel inline-progress">
              <div className="progress-copy"><strong>{progressLabel}</strong><span>{progress}%</span></div>
              <div className="progress-track"><div style={{ width: `${progress}%` }} /></div>
              <p className="muted small-text">Ne ferme pas cet onglet pendant la génération du fichier.</p>
            </div>
          )}

          <button className="button primary full" disabled={busy || !contentPackId}>
            {busy ? 'Publication en cours…' : 'Générer et publier'}
          </button>
        </form>

        <aside className="panel release-explanation">
          <p className="eyebrow">Fonctionnement</p>
          <h2>Publication atomique</h2>
          <ol>
            <li>Les énigmes approuvées sont lues depuis PostgreSQL.</li>
            <li>Une base SQLite est générée dans le navigateur.</li>
            <li>Le fichier est compressé, signé par SHA-256 puis envoyé dans Storage.</li>
            <li>Le manifeste racine est remplacé en dernier.</li>
            <li>Findex détecte ensuite la nouvelle version.</li>
          </ol>
          <div className="notice">
            La clé <code>service_role</code> n’est jamais utilisée dans cette interface. Les droits reposent sur ton compte Auth et les règles RLS.
          </div>
        </aside>
      </section>

      <section className="panel table-panel">
        <div className="panel-heading"><div><p className="eyebrow">Historique</p><h2>Versions générées</h2></div></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>Version</th><th>Nom</th><th>Statut</th><th>Schéma</th><th>Créée</th><th>Publiée</th><th>Manifeste</th></tr></thead>
            <tbody>
              {releases.length === 0 ? (
                <tr><td colSpan={7} className="empty-cell">Aucune publication.</td></tr>
              ) : releases.map((release) => {
                const manifestUrl = release.manifest_path
                  ? supabase.storage.from(CONTENT_BUCKET).getPublicUrl(release.manifest_path).data.publicUrl
                  : null;
                return (
                  <tr key={release.id}>
                    <td><strong>#{release.id}</strong></td>
                    <td>{release.name}<span className="row-subtitle">{release.notes || 'Aucune note'}</span></td>
                    <td><StatusBadge status={release.status} /></td>
                    <td>v{release.schema_version}</td>
                    <td>{formatDate(release.created_at)}</td>
                    <td>{formatDate(release.published_at)}</td>
                    <td>{manifestUrl ? <a className="text-link" href={manifestUrl} target="_blank" rel="noreferrer">Ouvrir</a> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
